import React, { useEffect, useState } from 'react';

function ShortenUrlSettings({ token }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [testResult, setTestResult] = useState(null);

  const [isEnabled, setIsEnabled] = useState(false);
  const [provider, setProvider] = useState('PIGMYURL');
  const [apiUrl, setApiUrl] = useState('https://pigmyurl.com/api/short_url_create');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [apiKeyChanged, setApiKeyChanged] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [aliasPrefix, setAliasPrefix] = useState('');
  const [expireType, setExpireType] = useState('day');
  const [expireValue, setExpireValue] = useState(7);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    ...extra
  });

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/url-shortener', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data?.error || 'Failed to load config');
      if (data) {
        setIsEnabled(data.is_enabled || false);
        setProvider(data.provider || 'PIGMYURL');
        setApiUrl(data.api_url || 'https://pigmyurl.com/api/short_url_create');
        setApiKeyMasked(data.api_key_masked || '');
        setAliasPrefix(data.custom_alias_prefix || '');
        setExpireType(data.default_expire_type || 'day');
        setExpireValue(data.default_expire_value || 7);
        setHasExistingConfig(true);
      } else {
        setHasExistingConfig(false);
      }
      setApiKey('');
      setApiKeyChanged(false);
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadConfig();
  }, [token]);

  const handleSave = async () => {
    setError('');
    setMessage('');
    if (!apiKey && !hasExistingConfig) {
      return setError('API Key is required');
    }
    setSaving(true);
    try {
      const body = {
        is_enabled: isEnabled,
        provider,
        api_url: apiUrl,
        custom_alias_prefix: aliasPrefix || null,
        default_expire_type: expireType,
        default_expire_value: Number(expireValue) || 7
      };
      if (apiKeyChanged && apiKey) {
        body.api_key = apiKey;
      }
      const res = await fetch('/api/admin/url-shortener', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) return setError(data?.error || 'Failed to save');
      setMessage('Settings saved successfully');
      setApiKey('');
      setApiKeyChanged(false);
      setHasExistingConfig(true);
      loadConfig();
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    setError('');
    const keyToTest = apiKeyChanged && apiKey ? apiKey : '';
    if (!keyToTest && !hasExistingConfig) {
      return setError('Enter an API Key first');
    }
    if (!keyToTest) {
      return setError('Enter or update the API Key to test connectivity');
    }
    setTesting(true);
    try {
      const res = await fetch('/api/admin/url-shortener/test', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ api_url: apiUrl, api_key: keyToTest })
      });
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, error: 'Network error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.2rem' }}>Shorten URLs</h2>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', marginTop: 0, marginBottom: '1.2rem' }}>
        Configure a URL shortener provider for generating SMS-friendly short links (pre-check-in, guest portal, etc.)
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginBottom: '0.8rem' }}>{message}</div>}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>Loading...</div>
      ) : (
        <div className="card" style={{ padding: '1.2rem' }}>
          {/* Enable Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1.2rem', paddingBottom: '1rem', borderBottom: '1px solid var(--theme-border)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' }}>
              <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
              Enable URL Shortening
            </label>
            <span style={{ fontSize: '0.78rem', color: isEnabled ? 'var(--theme-success)' : 'var(--theme-text-secondary)', fontWeight: 600 }}>
              {isEnabled ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Provider */}
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0.8rem 1rem', alignItems: 'center', fontSize: '0.88rem' }}>
            <label className="form-label" style={{ margin: 0 }}>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} style={{ padding: '0.45rem 0.5rem' }}>
              <option value="PIGMYURL">PigmyURL</option>
            </select>

            {/* API URL */}
            <label className="form-label" style={{ margin: 0 }}>API URL</label>
            <input type="text" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)}
              placeholder="https://pigmyurl.com/api/short_url_create"
              style={{ padding: '0.45rem 0.5rem' }} />

            {/* API Key */}
            <label className="form-label" style={{ margin: 0 }}>API Key</label>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKeyChanged ? apiKey : ''}
                  placeholder={hasExistingConfig ? `Current key: ${apiKeyMasked}` : 'Enter API key'}
                  onChange={(e) => { setApiKey(e.target.value); setApiKeyChanged(true); }}
                  style={{ padding: '0.45rem 2.2rem 0.45rem 0.5rem', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--theme-text-secondary)', padding: '0.2rem' }}
                  title={showApiKey ? 'Hide' : 'Show'}
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Alias Prefix */}
            <label className="form-label" style={{ margin: 0 }}>Alias Prefix</label>
            <div>
              <input type="text" value={aliasPrefix} onChange={(e) => setAliasPrefix(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
                placeholder="e.g., HTLGOA" maxLength={20}
                style={{ padding: '0.45rem 0.5rem', width: 180 }} />
              <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginTop: '0.2rem' }}>
                Short URLs will use: {aliasPrefix || 'PREFIX'}-RES103
              </div>
            </div>

            {/* Default Expiry */}
            <label className="form-label" style={{ margin: 0 }}>Default Expiry</label>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="number" min={1} max={365} value={expireValue}
                onChange={(e) => setExpireValue(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                style={{ padding: '0.45rem 0.5rem', width: 80 }} />
              <select value={expireType} onChange={(e) => setExpireType(e.target.value)} style={{ padding: '0.45rem 0.5rem' }}>
                <option value="minute">Minute(s)</option>
                <option value="hour">Hour(s)</option>
                <option value="day">Day(s)</option>
              </select>
            </div>
          </div>

          {/* Test Connection */}
          <div style={{ marginTop: '1.2rem', paddingTop: '1rem', borderTop: '1px solid var(--theme-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
              <button onClick={handleTest} disabled={testing} className="btn-muted btn-sm rounded">
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              {testResult && (
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: testResult.success ? 'var(--theme-success)' : 'var(--theme-danger)' }}>
                  {testResult.success
                    ? <>Success: <a href={testResult.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--theme-primary)' }}>{testResult.short_url}</a></>
                    : <>Failed: {testResult.error}</>
                  }
                </span>
              )}
            </div>
          </div>

          {/* Save */}
          <div style={{ marginTop: '1.2rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSave} disabled={saving} className="btn-primary rounded" style={{ padding: '0.5rem 1.5rem' }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortenUrlSettings;
